import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';

import { ReactComponent as SearchIcon } from 'common/dist/assets/search.svg';

import { api } from 'common';

import * as cache from '../cache';
import { CategoryType, SearchItem, StyledTypography } from '../index';
import SearchProviderItem from './SearchProviderItem';
import SearchResultItem from './SearchResultItem';
import EmptySearch from './EmptySearch';
import { SearchContainer } from './LocationSearch';

import { useSelector } from 'react-redux';
import * as store from '../../../../../reducers/store';
import classNames from 'classnames';

export interface SearchCategory {
  name: string;
  items: SearchItem[];
}

type SafeProviderWithPracticesAndFullName =
  api.PrevisitProvider.SafeProviderWithPractices & { fullName: string };

interface Props {
  specialtyIconColor: string;
  initialSpecialtySearch?: api.Specialty;
  onSpecialtyChange: (specialty?: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  setCurrentPage?: (value: number) => void;
}

const SpecialtyProviderSearch: React.FC<Props> = ({
  specialtyIconColor,
  initialSpecialtySearch,
  onSpecialtyChange,
  onFocus,
  onBlur,
  setCurrentPage,
}) => {
  const organizationId = useSelector(
    (store: store.All) => store.inbound.organizationId,
  );
  const [inputValue, setInputValue] = useState<string>(
    initialSpecialtySearch
      ? api.Common.SpecialtyMap[initialSpecialtySearch]
      : '',
  );
  const [searchItems, setSearchItems] = useState<SearchItem[]>([]);
  const [focus, setFocus] = useState<boolean>(false);
  const [specialty, setSpecialty] = useState<string>();
  const [supportedSpecialties, setSupportedSpecialties] = useState<
    api.Encounter.Specialty[]
  >([]);
  const [allProviders, setAllProviders] =
    useState<SafeProviderWithPracticesAndFullName[]>();
  const [suggestedProviders, setSuggestedProviders] =
    useState<api.PrevisitProvider.SafeProviderWithPractices[]>();
  const [provider, setProvider] =
    useState<api.PrevisitProvider.SafeProviderWithPractices>();

  useEffect(() => {
    async function retrieveProviders() {
      const { providers, supportedSpecialties } =
        await cache.findAllProviders(organizationId);
      setSupportedSpecialties(supportedSpecialties);
      setAllProviders(
        providers.map((provider) => {
          return {
            ...provider,
            fullName: provider.firstName + ' ' + provider.lastName,
          };
        }),
      );
    }
    retrieveProviders();
  }, [organizationId]);

  const categories: SearchCategory[] = [
    {
      name: 'Supported Medical Specialties',
      items: supportedSpecialties.map((specialty) => {
        return {
          categoryType: CategoryType.Specialty,
          name: api.Common.SpecialtyMap[specialty],
        };
      }),
    },
  ];

  const initialCategories = categories.flatMap((category) => {
    return category.items;
  });

  const handleItemClick = (item: SearchItem) => {
    setFocus(false);
    if (item.categoryType === CategoryType.Specialty) {
      setSpecialty(item.name);
      setInputValue(item.name);
      onSpecialtyChange(api.Common.InvertedSpecialtyMap[item.name]);
      setCurrentPage?.(1);
    }
  };

  const handleProviderClick = (
    provider: api.PrevisitProvider.SafeProviderWithPractices,
  ) => {
    setProvider(provider);
  };

  const itemResults = () => {
    const itemCount = searchItems.length;
    if (itemCount === 0) {
      return null;
    }
    return (
      <div
        className={classNames('pt-3', {
          'pb-3': !!suggestedProviders && suggestedProviders.length > 0,
        })}
      >
        {searchItems.map((item) => {
          return (
            <SearchResultItem
              key={item.name}
              inputValue={inputValue}
              item={item}
              handleItemClick={handleItemClick}
            />
          );
        })}
      </div>
    );
  };

  const providerResults = () => {
    if (!suggestedProviders || suggestedProviders?.length === 0) {
      return null;
    }
    const providerItemsResult = suggestedProviders?.map((suggestion) => {
      return (
        <SearchProviderItem
          key={suggestion.id}
          inputValue={inputValue}
          suggestion={suggestion}
          handleProviderClick={handleProviderClick}
        />
      );
    });

    return <ProviderResults>{providerItemsResult}</ProviderResults>;
  };

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    setSpecialty(undefined);
    if (newValue.length > 0) {
      const debounceFilter = _.debounce(() => {
        const suggestions = getSpecialtySuggestions(
          initialCategories,
          newValue,
        );
        const suggestedProviders = allProviders?.filter((suggestion) => {
          return suggestion.fullName
            .toLowerCase()
            .includes(newValue.toLowerCase());
        });
        setSearchItems(suggestions);
        setSuggestedProviders(suggestedProviders);
      }, 150);
      debounceFilter();
    }
    setInputValue(newValue);
    if (newValue === '') {
      setSearchItems([]);
      setSuggestedProviders(undefined);
    }
  };

  const results = () => {
    if (!focus) {
      return null;
    }
    if (
      inputValue !== '' &&
      (searchItems.length > 0 ||
        (suggestedProviders && suggestedProviders.length > 0))
    ) {
      return (
        <ResultsContainer>
          {itemResults()}
          {!!searchItems?.length && <StyledHR />}
          {providerResults()}
        </ResultsContainer>
      );
    } else if (searchItems.length === 0 && inputValue.length > 0) {
      return <EmptySearch inputValue={inputValue} />;
    }
    const results = categories.map((category, index) => {
      return (
        <ResultsContainer key={category.name}>
          <ResultsTitle>
            <StyledTypography
              variant="title-medium"
              bold={true}
              color={'var(--color-content-default-dark)'}
            >
              {category.name}
            </StyledTypography>
          </ResultsTitle>
          {category.items.map((item) => {
            return (
              <SearchResultItem
                key={item.name}
                inputValue={inputValue}
                item={item}
                handleItemClick={handleItemClick}
              />
            );
          })}
        </ResultsContainer>
      );
    });
    return results;
  };

  return (
    <>
      <SearchContainer>
        <StyledSearchIcon color={specialtyIconColor} />
        <StyledTextField
          placeholder={`Search specialty ${
            allProviders && allProviders.length > 0 ? 'or provider' : ''
          }`}
          value={specialty ?? inputValue}
          onMouseDown={() => {
            const suggestions = getSpecialtySuggestions(
              initialCategories,
              inputValue,
            );
            setSpecialty(undefined);
            setSearchItems(suggestions);
            onSpecialtyChange();
            setInputValue('');
          }}
          onFocus={(e) => {
            if (onFocus) {
              onFocus();
            }
            setFocus(true);
            e.target.select();
          }}
          onBlur={() => {
            if (onBlur) {
              onBlur();
            }
            if (!provider) {
              setFocus(false);
            }
          }}
          inputMode="text"
          onChange={(event) => updateInputValue(event)}
        />
      </SearchContainer>
      {focus ? <Results>{results()}</Results> : null}
    </>
  );
};

export default SpecialtyProviderSearch;

const getSpecialtySuggestions = (
  categories: SearchItem[],
  inputValue: string,
) => {
  return categories.filter((suggestion) => {
    return suggestion.name.toLowerCase().includes(inputValue.toLowerCase());
  });
};

const StyledSearchIcon = styled(SearchIcon).attrs<{ color: string }>(
  (props) => ({
    color: props.color || 'var(--color-content-disabled)',
  }),
)`
  stroke-width: 1;
  color: ${(props) => props.color};
`;

export const StyledTextField = styled.input`
  margin-left: 0.5rem;
  width: 100%;
  border-radius: 0;
  border: none;
  outline: none;
  font-size: 16px;
  line-height: 125%;
  height: 24px;

  :focus {
    border-radius: 0;
    border: none;
    outline: none;
  }

  &::placeholder {
    color: var(--color-content-disabled);
  }
`;

const ResultsContainer = styled.div`
  width: 100%;
  padding-bottom: 24px;
`;

export const Results = styled.div`
  margin-top: 0.25rem;
  width: 100%;
  background-color: var(--color-white);
  box-shadow: 0px 16px 40px 0px #25275224;
  max-height: 360px;
  overflow-y: auto;

  display: flex;
  position: absolute;
  z-index: 1;
`;

const ProviderResults = styled.div`
  display: block;
  overflow-y: auto;
  padding-top: 12px;
`;

const ResultsTitle = styled.div`
  padding: 22px 16px 8px;
`;

const StyledHR = styled.hr`
  color: var(--color-border-default-light);
  margin: 0 1rem;
  opacity: 1;
`;
